import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Slider,
  Switch,
  Rating,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocationOn as LocationIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

// Import enhanced widgets (excluding GoogleMapsAddressPicker)
import {
  PhoneInput,
  CurrencyInput,
  EnhancedSlider,
  EnhancedRating,
  ColorPicker,
  ToggleSwitch,
  CheckboxGroup,
  EnhancedAutocomplete,
  FileUpload,
  DataGrid,
  GroupBox,
  RichTextEditor,
  CodeEditor,
} from './EnhancedWidgets';
import { LoanBalanceSlider, EligibilityChecklist, DiagnosticDiagram, CameraAnnotation } from './useCases';

// Standalone Google Maps Address Field Component
function GoogleMapsAddressField({
  label,
  value = '',
  onChange,
  placeholder = 'Search for an address...',
  apiKey,
  disabled = false,
  required = false,
  fullWidth = true,
  ...props
}) {
  const [searchValue, setSearchValue] = useState(value);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [googleFailed, setGoogleFailed] = useState(false);
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Initialize Google Maps API
  useEffect(() => {
    if (!apiKey) {
      console.warn('Google Maps API key not provided. Address picker will work in basic mode.');
      return;
    }

    if (!window.google) {
      // First, test the API key directly
      const testUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=test&key=${apiKey}`;
      console.log('GoogleMapsAddressField - Testing API key with URL:', testUrl);

      fetch(testUrl)
        .then((response) => {
          console.log('GoogleMapsAddressField - API key test response:', response.status, response.statusText);
          return response.json();
        })
        .then((data) => {
          console.log('GoogleMapsAddressField - API key test data:', data);
          if (data.status === 'REQUEST_DENIED') {
            console.error('GoogleMapsAddressField - API key is invalid or has restrictions:', data.error_message);
            setGoogleFailed(true);
            return;
          }

          // If API key test passes, load the Maps JavaScript API
          // Check if script is already loaded to prevent duplicates
          const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
          if (existingScript) {
            console.log('GoogleMapsAddressField - Google Maps API script already exists, reusing');
            setGoogleLoaded(true);
            setTimeout(() => {
              console.log('GoogleMapsAddressField - Trying initialization with existing script');
              initializeAutocomplete();
            }, 100);
            return;
          }

          const script = document.createElement('script');
          const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
          console.log('GoogleMapsAddressField - Loading Google Maps API from:', scriptUrl);
          script.src = scriptUrl;
          script.async = true;
          script.defer = true;
          script.onload = () => {
            console.log('GoogleMapsAddressField - Google Maps API loaded successfully');
            setGoogleLoaded(true);
            // Try immediate initialization with longer delay for stability
            setTimeout(() => {
              console.log('GoogleMapsAddressField - Trying immediate initialization after API load');
              try {
                initializeAutocomplete();
              } catch (error) {
                console.error('GoogleMapsAddressField - Initialization failed:', error);
                setGoogleFailed(true);
              }
            }, 200);
          };
          script.onerror = (error) => {
            console.error('GoogleMapsAddressField - Failed to load Google Maps API:', error);
            console.error('GoogleMapsAddressField - Script URL was:', scriptUrl);
            console.error('GoogleMapsAddressField - Error details:', {
              type: error.type,
              target: error.target,
              currentTarget: error.currentTarget,
              timeStamp: error.timeStamp,
              isTrusted: error.isTrusted,
            });

            // Try to fetch the script URL to see if it's accessible
            fetch(scriptUrl)
              .then((response) => {
                console.log('GoogleMapsAddressField - Script URL fetch response:', response.status, response.statusText);
              })
              .catch((fetchError) => {
                console.error('GoogleMapsAddressField - Script URL fetch error:', fetchError);
              });

            // Set failed state to show fallback
            setGoogleFailed(true);
          };
          document.head.appendChild(script);
        })
        .catch((error) => {
          console.error('GoogleMapsAddressField - API key test failed:', error);
          setGoogleFailed(true);
        });
    } else {
      console.log('GoogleMapsAddressField - Google Maps API already loaded');
      setGoogleLoaded(true);
      // Try immediate initialization with error handling
      setTimeout(() => {
        console.log('GoogleMapsAddressField - Trying immediate initialization (API already loaded)');
        try {
          initializeAutocomplete();
        } catch (error) {
          console.error('GoogleMapsAddressField - Initialization failed (API already loaded):', error);
          setGoogleFailed(true);
        }
      }, 100);
    }
  }, [apiKey]);

  // Initialize map when location is selected
  useEffect(() => {
    if (selectedLocation && googleLoaded && !mapLoaded) {
      initializeMap();
    }
  }, [selectedLocation, googleLoaded, mapLoaded]);

  // Delayed initialization to ensure DOM is ready
  useEffect(() => {
    if (googleLoaded && inputRef.current) {
      console.log('GoogleMapsAddressField - Delayed initialization triggered');
      console.log('GoogleMapsAddressField - googleLoaded:', googleLoaded);
      console.log('GoogleMapsAddressField - inputRef.current:', inputRef.current);

      // Add a small delay to ensure the DOM is fully rendered
      const timer = setTimeout(() => {
        console.log('GoogleMapsAddressField - Executing delayed initialization');
        initializeAutocomplete();
      }, 100);

      return () => clearTimeout(timer);
    }
    console.log('GoogleMapsAddressField - Delayed initialization conditions not met');
    console.log('GoogleMapsAddressField - googleLoaded:', googleLoaded);
    console.log('GoogleMapsAddressField - inputRef.current:', inputRef.current);
  }, [googleLoaded]);

  const initializeAutocomplete = () => {
    if (inputRef.current && window.google && window.google.maps) {
      try {
        console.log('GoogleMapsAddressField - Starting input element detection...');
        console.log('GoogleMapsAddressField - inputRef.current:', inputRef.current);
        console.log('GoogleMapsAddressField - inputRef.current.tagName:', inputRef.current.tagName);
        console.log('GoogleMapsAddressField - inputRef.current.innerHTML:', inputRef.current.innerHTML);

        // Get the actual input element - try multiple approaches
        let inputElement = null;

        // Try to get the input element from the ref
        if (inputRef.current.tagName === 'INPUT') {
          inputElement = inputRef.current;
          console.log('GoogleMapsAddressField - Direct input element found');
        } else if (inputRef.current.querySelector) {
          inputElement = inputRef.current.querySelector('input');
          console.log('GoogleMapsAddressField - Input element found via querySelector:', inputElement);
        }

        // If still no input element, try to find it in the DOM
        if (!inputElement) {
          const textFieldContainer = inputRef.current;
          if (textFieldContainer) {
            const inputs = textFieldContainer.getElementsByTagName('input');
            console.log('GoogleMapsAddressField - Found inputs via getElementsByTagName:', inputs.length);
            if (inputs.length > 0) {
              inputElement = inputs[0];
              console.log('GoogleMapsAddressField - Input element found via getElementsByTagName:', inputElement);
            }
          }
        }

        // Try to find input by looking for any input element within the container
        if (!inputElement) {
          console.log('GoogleMapsAddressField - Trying to find input element in document...');
          const allInputs = document.querySelectorAll('input');
          console.log('GoogleMapsAddressField - Total inputs in document:', allInputs.length);

          // Find the input that's closest to our container
          for (let i = 0; i < allInputs.length; i++) {
            const input = allInputs[i];
            if (inputRef.current.contains(input)) {
              inputElement = input;
              console.log('GoogleMapsAddressField - Found input element via document search:', inputElement);
              break;
            }
          }
        }

        if (!inputElement || inputElement.tagName !== 'INPUT') {
          console.error('GoogleMapsAddressField - Could not find valid input element');
          console.error('GoogleMapsAddressField - inputRef.current:', inputRef.current);
          console.error('GoogleMapsAddressField - inputElement:', inputElement);
          setGoogleFailed(true);
          return;
        }

        console.log('GoogleMapsAddressField - Found input element:', inputElement);
        console.log('GoogleMapsAddressField - Input element tagName:', inputElement.tagName);
        console.log('GoogleMapsAddressField - Input element type:', inputElement.type);

        // Initialize Google Maps Autocomplete
        if (window.google.maps.places && window.google.maps.places.Autocomplete) {
          console.log('GoogleMapsAddressField - Initializing Google Maps Autocomplete');

          // Create autocomplete instance
          const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
            types: ['address'],
            componentRestrictions: { country: 'us' },
          });

          // Store autocomplete reference
          autocompleteRef.current = autocomplete;

          // Add place_changed event listener
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            console.log('GoogleMapsAddressField - Place selected:', place);

            if (place.geometry) {
              const location = {
                address: place.formatted_address,
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                placeId: place.place_id,
              };

              setSelectedLocation(location);
              setSearchValue(place.formatted_address);
              onChange(place.formatted_address);
              setShowMap(true);
            }
          });

          console.log('GoogleMapsAddressField - Autocomplete initialized successfully');
        } else {
          console.warn('GoogleMapsAddressField - Places library not available, using basic input');
          setGoogleFailed(true);
        }
      } catch (error) {
        console.error('Error initializing Google Maps autocomplete:', error);
        setGoogleFailed(true);
      }
    }
  };

  const initializeMap = () => {
    if (mapRef.current && window.google && window.google.maps && selectedLocation) {
      try {
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
          zoom: 15,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        // Add a marker for the selected location
        new window.google.maps.Marker({
          position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
          map: mapInstance,
          title: selectedLocation.address,
        });

        setMapLoaded(true);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    }
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
    onChange({ target: { value } });
  };

  const handleAddressSubmit = () => {
    const address = searchValue;
    if (address && window.google && window.google.maps) {
      console.log('GoogleMapsAddressField - Submitting address:', address);
      // Use Geocoding service to get coordinates
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        console.log('GoogleMapsAddressField - Geocoding result:', status, results);
        if (status === 'OK' && results[0]) {
          const location = {
            address: results[0].formatted_address,
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            placeId: results[0].place_id,
          };
          console.log('GoogleMapsAddressField - Location found:', location);
          setSelectedLocation(location);
          setSearchValue(location.address);
          onChange({ target: { value: location.address } });
          setShowMap(true);
        } else {
          console.log('GoogleMapsAddressField - Geocoding failed:', status);
          // Show a simple alert for now
          alert('Address not found. Please try a different address.');
        }
      });
    } else {
      console.log('GoogleMapsAddressField - No address or Google Maps not available');
      alert('Please enter an address first.');
    }
  };

  const handleClearLocation = () => {
    setSearchValue('');
    setSelectedLocation(null);
    setShowMap(false);
    setMapLoaded(false);
    onChange({ target: { value: '' } });
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      {googleFailed && (
        <Box sx={{
          mb: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1,
        }}
        >
          <Typography variant="caption" color="warning.contrastText">
            ⚠️ Google Maps API failed to load. Using basic address input.
          </Typography>
        </Box>
      )}

      <TextField
        ref={inputRef}
        label={label}
        placeholder={placeholder}
        value={searchValue}
        onChange={handleSearchChange}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        variant="outlined"
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleAddressSubmit();
          }
        }}
        InputProps={{
          endAdornment: (
            <>
              {selectedLocation && (
                <IconButton
                  onClick={handleClearLocation}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <ClearIcon />
                </IconButton>
              )}
              <IconButton
                onClick={handleAddressSubmit}
                size="small"
                sx={{ mr: 1 }}
                title="Lookup address"
              >
                <LocationIcon />
              </IconButton>
            </>
          ),
        }}
        {...props}
      />

      {showMap && selectedLocation && googleLoaded && !googleFailed && (
        <Box sx={{
          mt: 2, height: 300, border: 1, borderColor: 'divider', borderRadius: 1,
        }}
        >
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </Box>
      )}

      {selectedLocation && (
        <Box sx={{
          mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1,
        }}
        >
          <Typography variant="caption" color="text.secondary">
            Selected:
            {' '}
            {selectedLocation.address}
          </Typography>
          {selectedLocation.lat && selectedLocation.lng && (
            <Typography variant="caption" display="block" color="text.secondary">
              Coordinates:
              {' '}
              {selectedLocation.lat.toFixed(6)}
              ,
              {' '}
              {selectedLocation.lng.toFixed(6)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function FormFieldRenderer({
  type = 'text',
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  options = [],
  min,
  max,
  step,
  multiline = false,
  rows = 1,
  maxRows = 4,
  onKeyDown,
  ...props
}) {
  const commonProps = {
    fullWidth: true,
    variant: 'outlined',
    disabled,
    required,
    ...props,
  };

  const handleChange = (newValue) => {
    if (onChange) {
      // Handle both direct values and event objects
      if (typeof newValue === 'object' && newValue.target) {
        onChange(newValue);
      } else {
        onChange({ target: { value: newValue } });
      }
    }
  };

  switch (type) {
    case 'text':
    case 'email':
    case 'password':
    case 'url':
      return (
        <TextField
          {...commonProps}
          type={type}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          onKeyDown={onKeyDown}
        />
      );

    case 'textarea':
      return (
        <TextField
          {...commonProps}
          multiline
          minRows={rows}
          maxRows={maxRows}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          onKeyDown={onKeyDown}
        />
      );

    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          inputProps={{
            min,
            max,
            step,
          }}
        />
      );

    case 'phone':
      return (
        <PhoneInput
          {...commonProps}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
        />
      );

    case 'currency':
      return (
        <CurrencyInput
          {...commonProps}
          label={label}
          value={value || ''}
          onChange={handleChange}
        />
      );

    case 'date':
      return (
        <TextField
          {...commonProps}
          type="date"
          label={label}
          value={value || ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'datetime':
      return (
        <TextField
          {...commonProps}
          type="datetime-local"
          label={label}
          value={value || ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'time':
      return (
        <TextField
          {...commonProps}
          type="time"
          label={label}
          value={value || ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => handleChange(e.target.checked)}
              disabled={disabled}
            />
          )}
          label={label}
        />
      );

    case 'switch':
      return (
        <ToggleSwitch
          {...commonProps}
          label={label}
          value={Boolean(value)}
          onChange={(checked) => handleChange(checked)}
        />
      );

    case 'slider':
      return (
        <Box>
          <Typography gutterBottom>{label}</Typography>
          <EnhancedSlider
            value={value || 0}
            onChange={(newValue) => handleChange(newValue)}
            min={min || 0}
            max={max || 100}
            step={step || 1}
            {...props}
          />
        </Box>
      );

    case 'rating':
      return (
        <Box>
          <Typography gutterBottom>{label}</Typography>
          <EnhancedRating
            value={value || 0}
            onChange={(newValue) => handleChange(newValue)}
            max={max || 5}
            {...props}
          />
        </Box>
      );

    case 'color':
      return (
        <Box>
          <Typography gutterBottom>{label}</Typography>
          <ColorPicker
            value={value || '#1976d2'}
            onChange={(newValue) => handleChange(newValue)}
            {...props}
          />
        </Box>
      );

    case 'radiobuttons':
      return (
        <FormControl component="fieldset">
          <FormLabel component="legend">{label}</FormLabel>
          <RadioGroup
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
          >
            {options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio disabled={disabled} />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case 'checkboxgroup':
      return (
        <CheckboxGroup
          {...commonProps}
          label={label}
          value={Array.isArray(value) ? value : []}
          onChange={(newValue) => handleChange(newValue)}
          options={options}
        />
      );

    case 'combobox':
    case 'listbox':
    case 'select':
      return (
        <FormControl {...commonProps}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value || ''}
            onChange={handleChange}
            label={label}
          >
            {options.map((option, index) => {
              // Handle both string options and object options with label/value
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;

              return (
                <MenuItem key={index} value={optionValue}>
                  {optionLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );

    case 'autocomplete':
      return (
        <EnhancedAutocomplete
          {...commonProps}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={(newValue) => handleChange(newValue)}
          options={options}
        />
      );

    case 'file':
      return (
        <FileUpload
          {...commonProps}
          label={label}
          onChange={(files) => handleChange(files)}
          accept={props.accept || '*/*'}
          multiple={props.multiple || false}
          maxSize={props.maxSize || '10MB'}
        />
      );

    case 'image':
      return (
        <FileUpload
          {...commonProps}
          label={label}
          onChange={(files) => handleChange(files)}
          accept="image/*"
          multiple={props.multiple || false}
          maxSize={props.maxSize || '5MB'}
          showPreview
        />
      );

    case 'grid':
      return (
        <DataGrid
          {...commonProps}
          label={label}
          value={Array.isArray(value) ? value : []}
          onChange={(newValue) => handleChange(newValue)}
          columns={props.columns || []}
        />
      );

    case 'richtext':
      return (
        <RichTextEditor
          {...commonProps}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          height={props.height || 200}
        />
      );

    case 'code':
      return (
        <CodeEditor
          {...commonProps}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          language={props.language || 'javascript'}
          height={props.height || 150}
        />
      );

    case 'address':
      return (
        <GoogleMapsAddressField
          {...commonProps}
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          apiKey={props.apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        />
      );

    case 'groupbox':
      return (
        <GroupBox
          {...commonProps}
          label={label}
          collapsible={props.collapsible || false}
          collapsed={props.collapsed || false}
        >
          {props.children}
        </GroupBox>
      );

    case 'loanbalance':
      return (
        <LoanBalanceSlider
          value={value}
          onChange={handleChange}
          label={label}
          incomeMin={props.incomeMin}
          incomeMax={props.incomeMax}
          debtMin={props.debtMin}
          debtMax={props.debtMax}
          step={props.step}
        />
      );

    case 'eligibilitychecklist':
      return (
        <EligibilityChecklist
          items={props.items || []}
          value={value}
          onChange={handleChange}
          label={label}
        />
      );

    case 'diagnosticdiagram':
      return (
        <DiagnosticDiagram
          value={value}
          onChange={handleChange}
          label={label}
          steps={props.steps}
        />
      );

    case 'cameraannotation':
      return (
        <CameraAnnotation
          value={value}
          onChange={handleChange}
          label={label}
          height={props.height}
        />
      );

    default:
      return (
        <TextField
          {...commonProps}
          type="text"
          label={label}
          placeholder={placeholder}
          value={value || ''}
          onChange={handleChange}
          onKeyDown={onKeyDown}
        />
      );
  }
}

export default FormFieldRenderer;
